import { Button, Stack, ThemeProvider } from '@mui/material';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { CustomerChildrenRelations, CustomerRelationType, UpdateCustomer } from '@vy/types';
import { type ChildCustomerOverview, type Customer, CUSTOMER_RELATION_TYPES } from '@vy/types';
import { isNotEmptyArray } from '@vy/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { vyTheme } from '../../../../../assets/themes/vyTheme';
import { ViewCustomerChildren } from '../ViewCustomerChildren';
import { VyFormSection } from '../../../../../components/design/layout';
import { AddCustomerChildModal } from '../AddCustomerChildModal/AddCustomerChildModal.tsx';

const BATCH_SIZE = 10;

type UpdateCustomerChildrenProps = {
  customerChildren?: Customer[];
  customerName: string;
  customerId: string;
  childrenRelations?: { childId: string; relationType: CustomerRelationType }[];
};

export const UpdateCustomerChildren: React.FC<UpdateCustomerChildrenProps> = ({
  customerChildren = [],
  customerId,
  customerName,
  childrenRelations = [],
}) => {
  const [selectedChildren, setSelectedChildren] = useState<ChildCustomerOverview[]>(
    buildSelectedChildren(customerId, customerChildren, []),
  );
  const [childRelationTypes, setChildRelationTypes] = useState<Map<string, CustomerRelationType>>(() =>
    buildInitialRelationTypesMap(customerChildren, childrenRelations),
  );

  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, selectedChildren.length));
  };

  const { setValue } = useFormContext<UpdateCustomer>();

  const isInitialMount = useRef(true);

  useEffect(() => {
    const childrenRelations = buildChildrenRelations(selectedChildren, childRelationTypes);
    setValue(
      'childrenRelations',
      childrenRelations,
      isInitialMount.current ? undefined : { shouldDirty: true },
    );
    isInitialMount.current = false;
  }, [selectedChildren, childRelationTypes]);

  const handleAddChild = useCallback((newCustomerChild: ChildCustomerOverview) => {
    setSelectedChildren((prev) => {
      if (prev.some((child) => child.id === newCustomerChild.id)) return prev;
      return [newCustomerChild, ...prev];
    });
    setChildRelationTypes((prev) =>
      new Map(prev).set(newCustomerChild.id, CUSTOMER_RELATION_TYPES.accumulateUsage),
    );
  }, []);

  const handleDeleteChild = useCallback((childId: string) => {
    setSelectedChildren((prev) => prev.filter((child) => child.id !== childId));
    setChildRelationTypes((prev) => {
      const updated = new Map(prev);
      updated.delete(childId);
      return updated;
    });
  }, []);

  const handleRelationTypeChange = useCallback((childId: string, newRelationType: CustomerRelationType) => {
    setChildRelationTypes((prev) => new Map(prev).set(childId, newRelationType));
  }, []);

  const relatedEntitiesTitle = `Related entities ${isNotEmptyArray(selectedChildren) ? `(${selectedChildren.length})` : ''}`;

  return (
    <ThemeProvider theme={vyTheme}>
      <VyFormSection
        title={relatedEntitiesTitle}
        actions={
          Boolean(selectedChildren?.length) && (
            <AddCustomerChildModal
              onChildSelect={handleAddChild}
              customerId={customerId}
              childrenToExclude={selectedChildren}
              childrenToAdd={buildSelectedChildren(customerId, customerChildren, [])}
            />
          )
        }
      >
        {selectedChildren.slice(0, visibleCount).map((customerChild) => {
          const relationType =
            childRelationTypes.get(customerChild.id) || CUSTOMER_RELATION_TYPES.accumulateUsage;
          return (
            <ViewCustomerChildren
              key={customerChild.id}
              customerName={customerName}
              customerChild={customerChild}
              relationType={relationType}
              onDeleteChild={handleDeleteChild}
              onRelationTypeChange={handleRelationTypeChange}
            />
          );
        })}

        {visibleCount < selectedChildren.length && (
          <Stack
            direction="row"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              marginTop: 2
            }}>
            <Button onClick={handleShowMore} startIcon={<ArrowDownIcon />} variant="outlined" size="small">
              Show next {Math.min(BATCH_SIZE, selectedChildren.length - visibleCount)} of{' '}
              {selectedChildren.length}
            </Button>
          </Stack>
        )}

        {selectedChildren?.length === 0 && (
          <AddCustomerChildModal
            onChildSelect={handleAddChild}
            customerId={customerId}
            childrenToExclude={selectedChildren}
            childrenToAdd={buildSelectedChildren(customerId, customerChildren, [])}
          />
        )}
      </VyFormSection>
    </ThemeProvider>
  );
};

function buildSelectedChildren(
  customerId: string,
  existingChildren: Customer[],
  potentialChildren: ChildCustomerOverview[],
): ChildCustomerOverview[] {
  const seen = new Set<string>([customerId]);
  const result: ChildCustomerOverview[] = [];

  for (const child of existingChildren) {
    if (!seen.has(child.id)) {
      seen.add(child.id);
      result.push({ id: child.id, name: child.name, externalId: child.externalId });
    }
  }

  for (const child of potentialChildren) {
    if (!seen.has(child.id)) {
      seen.add(child.id);
      result.push(child);
    }
  }

  return result;
}

function buildInitialRelationTypesMap(
  customerChildren: Customer[],
  childrenRelations: { childId: string; relationType: CustomerRelationType }[],
): Map<string, CustomerRelationType> {
  const initialMap = new Map<string, CustomerRelationType>();
  const relationTypesMap = new Map(childrenRelations.map((rel) => [rel.childId, rel.relationType]));

  customerChildren.forEach((child) => {
    const relationType = relationTypesMap.get(child.id) || CUSTOMER_RELATION_TYPES.accumulateUsage;
    initialMap.set(child.id, relationType);
  });

  return initialMap;
}

function buildChildrenRelations(
  selectedChildren: ChildCustomerOverview[],
  relationTypes: Map<string, CustomerRelationType>,
): NonNullable<CustomerChildrenRelations> {
  return selectedChildren.map((child) => ({
    id: child.id,
    relationType: relationTypes.get(child.id) || CUSTOMER_RELATION_TYPES.accumulateUsage,
  }));
}
